import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import API from "../config/Api";
import { PERMISSIONS } from "../config/permissions";
import { AuthContext } from "../context/AuthContext";
import { hasAnyPermission } from "../utils/permissions";
import "./DeploymentGate.css";

const STATIC_STATUS_URL = "/deployment-status.json";
const IDLE_DELAY_MS = 3000;
const ACTIVE_DELAY_MS = 1200;
const SUCCESS_DURATION_MS = 7000;
const SUCCESS_FRESHNESS_MS = 10 * 60 * 1000;
const RELOAD_DELAY_MS = 4500;

const updatePhases = [
    { id: "securing", label: "Sécurisation" },
    { id: "installing", label: "Installation" },
    { id: "verifying", label: "Vérification" },
];

const maintenancePhases = [
    { id: "securing", label: "Protection" },
    { id: "installing", label: "Intervention" },
    { id: "verifying", label: "Contrôle" },
];

const initialStatus = {
    maintenance: false,
    status: "idle",
    mode: "NONE",
    phase: "idle",
    deploymentId: null,
    message: "",
    updatedAt: null,
    completedAt: null,
    connectionLost: false,
    managed: false,
    source: "initial",
    revision: 0,
};

const apiUrl = (path) => {
    const base = String(API.defaults.baseURL || "/api").replace(/\/$/, "");
    return new URL(`${base}${path}`, window.location.origin).toString();
};

const inferMode = (payload, status, source) => {
    const explicit = String(payload?.mode || "").trim().toUpperCase();
    if (["UPDATE", "MAINTENANCE"].includes(explicit)) return explicit;
    if (source === "static" || ["updating", "deploying"].includes(status)) return "UPDATE";
    if (status === "maintenance") return "MAINTENANCE";
    return "NONE";
};

const normalizeStatus = (payload, source = "static") => {
    const status = String(payload?.status || (payload?.maintenance ? "maintenance" : "idle")).toLowerCase();
    const maintenance = payload?.maintenance === true || ["maintenance", "deploying", "updating"].includes(status);
    return {
        maintenance,
        status,
        mode: inferMode(payload, status, source),
        phase: String(payload?.phase || (maintenance ? "installing" : "idle")).toLowerCase(),
        deploymentId: payload?.deploymentId || payload?.operationId
            ? String(payload.deploymentId || payload.operationId)
            : null,
        message: String(payload?.message || ""),
        updatedAt: payload?.updatedAt || null,
        startedAt: payload?.startedAt || null,
        completedAt: payload?.completedAt || null,
        startedBy: payload?.startedBy || null,
        completedBy: payload?.completedBy || null,
        connectionLost: false,
        managed: payload?.managed === true,
        source,
        revision: Number(payload?.revision || 0),
    };
};

const statusTime = (status) => {
    const value = status.updatedAt || status.completedAt || status.startedAt;
    const parsed = value ? new Date(value).getTime() : 0;
    return Number.isFinite(parsed) ? parsed : 0;
};

const selectPreferredStatus = (statuses) => {
    const valid = statuses.filter(Boolean);
    if (valid.length === 0) return null;
    const active = valid.filter((status) => status.maintenance);
    const candidates = active.length ? active : valid;
    return [...candidates].sort((left, right) => {
        const byTime = statusTime(right) - statusTime(left);
        if (byTime !== 0) return byTime;
        return Number(right.managed) - Number(left.managed);
    })[0];
};

const isRecentCompletion = (completedAt, now = Date.now()) => {
    if (!completedAt) return true;
    const completedTime = new Date(completedAt).getTime();
    return Number.isFinite(completedTime)
        && completedTime <= now + 60 * 1000
        && now - completedTime <= SUCCESS_FRESHNESS_MS;
};

const phaseIndex = (phase) => {
    const aliases = {
        preparing: "securing",
        backup: "securing",
        migrating: "installing",
        restarting: "installing",
        healthcheck: "verifying",
    };
    const normalized = aliases[phase] || phase;
    const index = updatePhases.findIndex((item) => item.id === normalized);
    return index < 0 ? 1 : index;
};

const DeploymentGate = ({
    statusUrl,
    idleDelayMs = IDLE_DELAY_MS,
    activeDelayMs = ACTIVE_DELAY_MS,
    successDurationMs = SUCCESS_DURATION_MS,
    reloadDelayMs = RELOAD_DELAY_MS,
    reloadOnUpdate = true,
}) => {
    const auth = useContext(AuthContext);
    const user = auth?.user;
    const [deployment, setDeployment] = useState(initialStatus);
    const [successNotice, setSuccessNotice] = useState(null);
    const [completing, setCompleting] = useState(false);
    const [operatorError, setOperatorError] = useState("");
    const [loginAccess, setLoginAccess] = useState(false);
    const stateRef = useRef(initialStatus);
    const successTimerRef = useRef(null);
    const reloadTimerRef = useRef(null);
    const panelRef = useRef(null);

    const canManage = hasAnyPermission(user, [
        PERMISSIONS.ADMIN,
        PERMISSIONS.TEAM_IT_MANAGER,
        PERMISSIONS.TEAM_IT_PROGRAMMATION,
    ]);
    const overlayVisible = deployment.maintenance && !loginAccess;

    const pollingTargets = useMemo(() => statusUrl
        ? [{ url: statusUrl, source: "static" }]
        : [
            { url: apiUrl("/system/status"), source: "api" },
            { url: STATIC_STATUS_URL, source: "static" },
        ], [statusUrl]);

    const showSuccess = useCallback((next, previous) => {
        if (!next.deploymentId || next.status !== "success" || !isRecentCompletion(next.completedAt)) return;
        const storageKey = `soma-system-success-${next.deploymentId}`;
        try {
            if (window.sessionStorage.getItem(storageKey) === "seen") return;
            window.sessionStorage.setItem(storageKey, "seen");
        } catch {
            // The notice still works when browser storage is unavailable.
        }
        const shouldReload = reloadOnUpdate && (reloadTimerRef.current != null
            || (previous?.maintenance && previous.mode === "UPDATE"));
        setSuccessNotice({ ...next, shouldReload });
        window.clearTimeout(successTimerRef.current);
        successTimerRef.current = window.setTimeout(() => setSuccessNotice(null), successDurationMs);
        // API and static status can confirm the same update under different identifiers.
        if (shouldReload && reloadTimerRef.current == null) {
            reloadTimerRef.current = window.setTimeout(() => window.location.reload(), reloadDelayMs);
        }
    }, [reloadDelayMs, reloadOnUpdate, successDurationMs]);

    const applyStatus = useCallback((next) => {
        if (!next) return;
        const previous = stateRef.current;
        stateRef.current = next;
        setDeployment(next);
        setOperatorError("");
        window.dispatchEvent(new CustomEvent("soma:system-status", { detail: next }));
        if (!next.maintenance && next.status === "success") showSuccess(next, previous);
    }, [showSuccess]);

    useEffect(() => {
        let mounted = true;
        let pollTimer;
        let controllers = [];

        const schedule = (delay) => {
            window.clearTimeout(pollTimer);
            pollTimer = window.setTimeout(poll, delay);
        };

        async function poll() {
            controllers.forEach((controller) => controller.abort());
            controllers = pollingTargets.map(() => new AbortController());
            const results = await Promise.allSettled(pollingTargets.map(async (target, index) => {
                const separator = target.url.includes("?") ? "&" : "?";
                const response = await window.fetch(`${target.url}${separator}v=${Date.now()}`, {
                    cache: "no-store",
                    headers: { Accept: "application/json" },
                    signal: controllers[index].signal,
                });
                if (!response.ok) throw new Error(`Status HTTP ${response.status}`);
                return normalizeStatus(await response.json(), target.source);
            }));
            if (!mounted) return;
            const statuses = results.filter((result) => result.status === "fulfilled").map((result) => result.value);
            const next = selectPreferredStatus(statuses);
            if (next) applyStatus(next);
            else if (stateRef.current.maintenance) {
                const retained = { ...stateRef.current, connectionLost: true };
                stateRef.current = retained;
                setDeployment(retained);
            }
            schedule(stateRef.current.maintenance ? activeDelayMs : idleDelayMs);
        }

        const refreshWhenVisible = () => {
            if (document.visibilityState === "visible") poll();
        };

        poll();
        document.addEventListener("visibilitychange", refreshWhenVisible);
        window.addEventListener("online", poll);

        return () => {
            mounted = false;
            controllers.forEach((controller) => controller.abort());
            window.clearTimeout(pollTimer);
            document.removeEventListener("visibilitychange", refreshWhenVisible);
            window.removeEventListener("online", poll);
        };
    }, [activeDelayMs, applyStatus, idleDelayMs, pollingTargets]);

    useEffect(() => {
        if (statusUrl || typeof window.EventSource !== "function") return undefined;
        const stream = new window.EventSource(apiUrl("/system/status/stream"));
        const receive = (event) => {
            try { applyStatus(normalizeStatus(JSON.parse(event.data), "api")); } catch { /* Polling remains active. */ }
        };
        stream.addEventListener("system-status", receive);
        return () => {
            stream.removeEventListener("system-status", receive);
            stream.close();
        };
    }, [applyStatus, statusUrl]);

    useEffect(() => () => {
        window.clearTimeout(successTimerRef.current);
        window.clearTimeout(reloadTimerRef.current);
    }, []);

    useEffect(() => {
        if (user) setLoginAccess(false);
    }, [user]);

    useEffect(() => {
        const appRoot = document.getElementById("root");
        if (!overlayVisible || !appRoot) return undefined;

        const previousAriaHidden = appRoot.getAttribute("aria-hidden");
        appRoot.inert = true;
        appRoot.setAttribute("aria-hidden", "true");
        document.body.classList.add("soma-deployment-locked");
        window.requestAnimationFrame(() => panelRef.current?.focus());

        return () => {
            appRoot.inert = false;
            if (previousAriaHidden === null) appRoot.removeAttribute("aria-hidden");
            else appRoot.setAttribute("aria-hidden", previousAriaHidden);
            document.body.classList.remove("soma-deployment-locked");
        };
    }, [overlayVisible]);

    const completeOperation = async () => {
        setCompleting(true);
        setOperatorError("");
        try {
            const response = await API.post("/team-it/system-operation/complete", {});
            applyStatus(normalizeStatus(response.data, "api"));
        } catch (error) {
            setOperatorError(error?.response?.data?.message || error?.message || "Impossible de terminer l'intervention.");
        } finally {
            setCompleting(false);
        }
    };

    if (typeof document === "undefined") return null;

    const mode = deployment.mode === "MAINTENANCE" ? "MAINTENANCE" : "UPDATE";
    const phases = mode === "MAINTENANCE" ? maintenancePhases : updatePhases;
    const currentPhase = phaseIndex(deployment.phase);
    const maintenanceMessage = deployment.connectionLost
        ? "La connexion est momentanément interrompue. Le verrou reste actif jusqu'à la vérification complète."
        : deployment.message || "SOMA MASTER est momentanément verrouillé pour protéger les opérations en cours.";
    const title = mode === "MAINTENANCE" ? "Maintenance de l’application" : "Mise à jour en cours";

    return createPortal(
        <>
            {overlayVisible && (
                <div className={`soma-deployment-overlay is-${mode.toLowerCase()}`} role="alertdialog" aria-modal="true" aria-labelledby="soma-deployment-title">
                    <section className="soma-deployment-panel" ref={panelRef} tabIndex="-1">
                        <header className="soma-deployment-brand">
                            <span className="soma-deployment-mark" aria-hidden="true">
                                <img src="/assets/img/brand/soma-app-icon-512.png" alt="" />
                            </span>
                            <span className="soma-deployment-brand-name">SOMA MASTER</span>
                            <span className={`soma-deployment-live ${deployment.connectionLost ? "is-offline" : ""}`}>
                                <i className={`bx ${deployment.connectionLost ? "bx-wifi-off" : "bx-broadcast"}`} aria-hidden="true"></i>
                                {deployment.connectionLost ? "Signal interrompu" : "Suivi en direct"}
                            </span>
                        </header>

                        <div className="soma-deployment-copy">
                            <span className="soma-deployment-status">
                                <i className={`bx ${mode === "MAINTENANCE" ? "bx-wrench" : "bx-cloud-upload"}`} aria-hidden="true"></i>
                                {mode === "MAINTENANCE" ? "Intervention technique sécurisée" : "Déploiement sécurisé"}
                            </span>
                            <h1 id="soma-deployment-title">{title}</h1>
                            <p>{maintenanceMessage}</p>
                        </div>

                        <div className="soma-deployment-progress">
                            <span
                                className="soma-deployment-progress-track"
                                role="progressbar"
                                aria-label="Progression des étapes"
                                aria-valuemin={1}
                                aria-valuemax={phases.length}
                                aria-valuenow={currentPhase + 1}
                                aria-valuetext={`Étape ${currentPhase + 1} sur ${phases.length} : ${phases[currentPhase].label}`}
                            >
                                <span style={{ transform: `scaleX(${(currentPhase + 1) / phases.length})` }}></span>
                            </span>
                            <ol className="soma-deployment-steps">
                                {phases.map((phase, index) => {
                                    const isComplete = index < currentPhase;
                                    const isCurrent = index === currentPhase;
                                    return (
                                        <li
                                            className={`${isComplete ? "is-complete" : ""} ${isCurrent ? "is-current" : ""}`}
                                            aria-current={isCurrent ? "step" : undefined}
                                            key={phase.id}
                                        >
                                            <span className="soma-deployment-step-icon" aria-hidden="true">
                                                <i className={`bx ${isComplete ? "bx-check" : isCurrent ? "bx-right-arrow-alt" : "bx-time-five"}`}></i>
                                            </span>
                                            <span className="soma-deployment-step-copy">
                                                <small>Étape {index + 1}</small>
                                                <strong>{phase.label}</strong>
                                            </span>
                                        </li>
                                    );
                                })}
                            </ol>
                        </div>

                        {deployment.startedBy && (
                            <div className="soma-deployment-meta">
                                <i className="bx bx-user-check" aria-hidden="true"></i>
                                <span>Lancée par <strong>{deployment.startedBy}</strong></span>
                            </div>
                        )}

                        {canManage && deployment.managed && (
                            <div className="soma-deployment-operator">
                                <div>
                                    <strong>Controle TEAM IT</strong>
                                    <span>Terminez l’intervention après vos vérifications.</span>
                                </div>
                                <button type="button" onClick={completeOperation} disabled={completing}>
                                    <i className={`bx ${completing ? "bx-loader-alt bx-spin" : "bx-check-shield"}`} aria-hidden="true"></i>
                                    {completing ? "Clôture…" : "Terminer"}
                                </button>
                                {operatorError && <p role="alert">{operatorError}</p>}
                            </div>
                        )}

                        {!user && deployment.managed && (
                            <div className="soma-deployment-login-access">
                                <div>
                                    <strong>Intervention TEAM IT</strong>
                                    <span>La connexion reste accessible uniquement pour clôturer l’intervention.</span>
                                </div>
                                <button type="button" onClick={() => setLoginAccess(true)}>
                                    <i className="bx bx-log-in" aria-hidden="true"></i>
                                    Connexion TEAM IT
                                </button>
                            </div>
                        )}

                        <footer className="soma-deployment-foot">
                            <i className="bx bx-shield-quarter" aria-hidden="true"></i>
                            <span>Aucune saisie n’est nécessaire. L’accès sera rétabli automatiquement.</span>
                        </footer>
                    </section>
                </div>
            )}

            {deployment.maintenance && loginAccess && !user && (
                <div className="soma-deployment-login-bridge" role="status">
                    <span><i className="bx bx-lock-alt" aria-hidden="true"></i> Intervention en cours</span>
                    <button type="button" onClick={() => setLoginAccess(false)}>Retour au verrou</button>
                </div>
            )}

            {successNotice && (
                <div className="soma-deployment-success" role="status" aria-live="polite">
                    <span aria-hidden="true"><i className="bx bx-check"></i></span>
                    <div>
                        <strong>{successNotice.mode === "MAINTENANCE" ? "Maintenance terminée" : "Mise à jour réussie"}</strong>
                        <p>{successNotice.message || "SOMA MASTER est de nouveau disponible."}</p>
                        {successNotice.shouldReload && <small>La nouvelle version va s’ouvrir automatiquement.</small>}
                    </div>
                    {successNotice.shouldReload && (
                        <button type="button" onClick={() => window.location.reload()} title="Actualiser maintenant" aria-label="Actualiser maintenant">
                            <i className="bx bx-refresh" aria-hidden="true"></i>
                        </button>
                    )}
                    {successNotice.shouldReload && <span className="soma-deployment-reload-progress" aria-hidden="true"></span>}
                </div>
            )}
        </>,
        document.body
    );
};

export { isRecentCompletion, normalizeStatus, phaseIndex, selectPreferredStatus };
export default DeploymentGate;
